import * as d3 from 'd3';
import { drawColonialGrillsOctagon } from './GrillsOctagon/drawColonialGrillsOctagon';

export function drawColonialGrillsTriangle(
  svg, x, y, width, height, frameWidth,
  horLines, verLines, lineColor, borderColor, borderWidth,
  grillThickness, shapeOrientation, innerPoints
) {
  if (!innerPoints || innerPoints.length !== 3) {
    console.error("drawColonialGrillsTriangle: innerPoints missing or invalid");
    return;
  }

  // Inner polygon points from shapeTriangle
  // For triangles: 3 points defining the inner triangle
  const [A2, B2, C2] = innerPoints;

  if (shapeOrientation === 'center') {
    // Center triangle: isosceles triangle pointing UP
    // A2 = bottom-left, B2 = top-center (apex), C2 = bottom-right
    const apexX = B2[0];
    const apexY = B2[1];
    const bottomLeftX = A2[0];
    const bottomLeftY = A2[1];
    const bottomRightX = C2[0];
    const bottomRightY = C2[1]; // same as A2[1]

    // ============================================================
    // VERTICAL GRILLS
    // ============================================================
    for (let i = 1; i <= verLines; i++) {
      const t = i / (verLines + 1);

      // X position along bottom edge
      const gx = bottomLeftX + t * (bottomRightX - bottomLeftX);

      // Determine which diagonal edge this grill intersects at the top
      let topY;

      if (gx < apexX) {
        // Left side: interpolate along left diagonal (A2 to B2)
        const t_left = (gx - bottomLeftX) / (apexX - bottomLeftX);
        topY = bottomLeftY + t_left * (apexY - bottomLeftY);
      } else {
        // Right side: interpolate along right diagonal (B2 to C2)
        const t_right = (gx - apexX) / (bottomRightX - apexX);
        topY = apexY + t_right * (bottomRightY - apexY);
      }

      const grillHeight = bottomLeftY - topY;

      if (grillHeight > 0) {
        svg.append('rect')
          .attr('x', gx - grillThickness / 2)
          .attr('y', topY)
          .attr('width', grillThickness)
          .attr('height', grillHeight)
          .attr('fill', lineColor)
          .attr('stroke', borderColor)
          .attr('stroke-width', borderWidth);
      }
    }

    // ============================================================
    // HORIZONTAL GRILLS
    // ============================================================
    for (let i = 1; i <= horLines; i++) {
      const gy = apexY + (bottomLeftY - apexY) * (i / (horLines + 1));

      // Calculate where this horizontal line intersects the two diagonal edges
      // Left diagonal: from A2 (bottomLeft) to B2 (apex)
      const t_left = (gy - apexY) / (bottomLeftY - apexY);
      const leftX = apexX + t_left * (bottomLeftX - apexX);

      // Right diagonal: from B2 (apex) to C2 (bottomRight)
      const t_right = (gy - apexY) / (bottomRightY - apexY);
      const rightX = apexX + t_right * (bottomRightX - apexX);

      const grillWidth = rightX - leftX;

      if (grillWidth > 0) {
        svg.append('rect')
          .attr('x', leftX)
          .attr('y', gy - grillThickness / 2)
          .attr('width', grillWidth)
          .attr('height', grillThickness)
          .attr('fill', lineColor)
          .attr('stroke', borderColor)
          .attr('stroke-width', borderWidth);
      }
    }
  }

  if (shapeOrientation === 'right') {
    // Right triangle: right angle at bottom-left
    // A2 = bottom-left, B2 = top-left, C2 = bottom-right
    const bottomLeftX = A2[0];
    const bottomLeftY = A2[1];
    const topLeftX = B2[0];
    const topLeftY = B2[1];
    const bottomRightX = C2[0];
    const bottomRightY = C2[1];

    // ============================================================
    // VERTICAL GRILLS
    // ============================================================
    for (let i = 1; i <= verLines; i++) {
      const t = i / (verLines + 1);

      // X position along bottom edge
      const gx = bottomLeftX + t * (bottomRightX - bottomLeftX);

      // Determine top Y by interpolating along diagonal (B2 to C2)
      let topY;

      if (gx < topLeftX) {
        // To the left of the vertical edge - full height
        topY = topLeftY;
      } else {
        // In the diagonal region: interpolate along diagonal from B2 to C2
        const t_diag = (gx - topLeftX) / (bottomRightX - topLeftX);
        topY = topLeftY + t_diag * (bottomRightY - topLeftY);
      }

      const grillHeight = bottomLeftY - topY;

      if (grillHeight > 0) {
        svg.append('rect')
          .attr('x', gx - grillThickness / 2)
          .attr('y', topY)
          .attr('width', grillThickness)
          .attr('height', grillHeight)
          .attr('fill', lineColor)
          .attr('stroke', borderColor)
          .attr('stroke-width', borderWidth);
      }
    }

    // ============================================================
    // HORIZONTAL GRILLS
    // ============================================================
    for (let i = 1; i <= horLines; i++) {
      const gy = topLeftY + (bottomLeftY - topLeftY) * (i / (horLines + 1));

      let startX = topLeftX;
      let endX;

      // Calculate where this horizontal line intersects the diagonal edge (B2 to C2)
      const t_diag = (gy - topLeftY) / (bottomRightY - topLeftY);
      endX = topLeftX + t_diag * (bottomRightX - topLeftX);

      const grillWidth = endX - startX;

      if (grillWidth > 0) {
        svg.append('rect')
          .attr('x', startX)
          .attr('y', gy - grillThickness / 2)
          .attr('width', grillWidth)
          .attr('height', grillThickness)
          .attr('fill', lineColor)
          .attr('stroke', borderColor)
          .attr('stroke-width', borderWidth);
      }
    }
  }

  if (shapeOrientation === 'left') {
    // Left triangle: right angle at bottom-right
    // A2 = bottom-right, B2 = top-right, C2 = bottom-left
    const bottomRightX = A2[0];
    const bottomRightY = A2[1];
    const topRightX = B2[0];
    const topRightY = B2[1];
    const bottomLeftX = C2[0];
    const bottomLeftY = C2[1];

    // ============================================================
    // VERTICAL GRILLS
    // ============================================================
    for (let i = 1; i <= verLines; i++) {
      const t = i / (verLines + 1);

      // X position along bottom edge
      const gx = bottomLeftX + t * (bottomRightX - bottomLeftX);

      // Determine top Y by interpolating along diagonal (C2 to B2)
      let topY;

      if (gx > topRightX) {
        // To the right of the vertical edge - full height
        topY = topRightY;
      } else {
        // In the diagonal region: interpolate along diagonal from C2 to B2
        const t_diag = (gx - bottomLeftX) / (topRightX - bottomLeftX);
        topY = bottomLeftY + t_diag * (topRightY - bottomLeftY);
      }

      const grillHeight = bottomRightY - topY;

      if (grillHeight > 0) {
        svg.append('rect')
          .attr('x', gx - grillThickness / 2)
          .attr('y', topY)
          .attr('width', grillThickness)
          .attr('height', grillHeight)
          .attr('fill', lineColor)
          .attr('stroke', borderColor)
          .attr('stroke-width', borderWidth);
      }
    }

    // ============================================================
    // HORIZONTAL GRILLS
    // ============================================================
    for (let i = 1; i <= horLines; i++) {
      const gy = topRightY + (bottomRightY - topRightY) * (i / (horLines + 1));

      let startX;
      let endX = topRightX;

      // Calculate where this horizontal line intersects the diagonal edge (C2 to B2)
      const t_diag = (gy - bottomLeftY) / (topRightY - bottomLeftY);
      startX = bottomLeftX + t_diag * (topRightX - bottomLeftX);

      const grillWidth = endX - startX;

      if (grillWidth > 0) {
        svg.append('rect')
          .attr('x', startX)
          .attr('y', gy - grillThickness / 2)
          .attr('width', grillWidth)
          .attr('height', grillThickness)
          .attr('fill', lineColor)
          .attr('stroke', borderColor)
          .attr('stroke-width', borderWidth);
      }
    }
  }
}
export function drawColonialGrillsRectangle(
  svg,
  x,
  y,
  width,
  height,
  frameWidth = 10,
  horLines = 1,
  verLines = 1,
  lineColor = 'white',
  borderColor = 'black',
  borderWidth = 0.2,
  grillThickness = 2
) {
  const glassX = x;
  const glassY = y;
  const glassWidth = width;
  const glassHeight = height;

  // Vertical grills
  for (let i = 1; i <= verLines; i++) {
    const gx = glassX + (glassWidth * i) / (verLines + 1);
    svg.append('rect')
      .attr('x', gx - grillThickness / 2)
      .attr('y', glassY)
      .attr('width', grillThickness)
      .attr('height', glassHeight)
      .attr('fill', lineColor)
      .attr('stroke', borderColor)
      .attr('stroke-width', borderWidth);
  }

  // Horizontal grills
  for (let i = 1; i <= horLines; i++) {
    const gy = glassY + (glassHeight * i) / (horLines + 1);
    svg.append('rect')
      .attr('x', glassX)
      .attr('y', gy - grillThickness / 2)
      .attr('width', glassWidth)
      .attr('height', grillThickness)
      .attr('fill', lineColor)
      .attr('stroke', borderColor)
      .attr('stroke-width', borderWidth);
  }
}
export function drawColonialGrillsAngledTopTrapezoid(
  svg, x, y, width, height, frameWidth,
  horLines, verLines, lineColor, borderColor, borderWidth,
  grillThickness, shapeOrientation, y1, y2, innerPoints
) {
  if (!innerPoints || innerPoints.length !== 4) {
    console.error("drawColonialGrillsAngledTopTrapezoid: innerPoints missing or invalid");
    return;
  }

  // Inner polygon points from shapeAngledTopTrapezoid
  const [A2, B2, C2, D2] = innerPoints;

  const innerLeftTopX = B2[0];
  const innerLeftTopY = B2[1];

  const innerRightTopX = C2[0];
  const innerRightTopY = C2[1];

  const innerBottomY = A2[1]; // same as D2[1]

  const innerLeftX = B2[0];
  const innerRightX = C2[0];

  // ============================================================
  // VERTICAL GRILLS — ALIGNED WITH INNER TOP EDGE
  // ============================================================
  for (let i = 1; i <= verLines; i++) {
    const t = i / (verLines + 1);

    const gx = innerLeftTopX + t * (innerRightTopX - innerLeftTopX);
    const topY = innerLeftTopY + t * (innerRightTopY - innerLeftTopY);

    const grillHeight = innerBottomY - topY;

    if (grillHeight > 0) {
      svg.append('rect')
        .attr('x', gx - grillThickness / 2)
        .attr('y', topY)
        .attr('width', grillThickness)
        .attr('height', grillHeight)
        .attr('fill', lineColor)
        .attr('stroke', borderColor)
        .attr('stroke-width', borderWidth);
    }
  }

  // ============================================================
  // HORIZONTAL GRILLS — ORIENTATION-AWARE CLIPPING
  // ============================================================
  const topSlope = innerRightTopY - innerLeftTopY; // >0: slopes down (right); <0: slopes up (left)

  // Find the highest and lowest points of the top edge
  const topHighestY = Math.min(innerLeftTopY, innerRightTopY);
  const topLowestY = Math.max(innerLeftTopY, innerRightTopY);

  for (let i = 1; i <= horLines; i++) {
    // Calculate gy from the highest point to ensure grills span the full range
    const gy = topHighestY + (innerBottomY - topHighestY) * (i / (horLines + 1));

    let startX = innerLeftX;
    let endX = innerRightX;

    // Clip left side if above the angled edge
    if (gy < innerLeftTopY) {
      // This horizontal line is above the left corner, need to clip from the angled edge
      const t = (gy - innerLeftTopY) / (innerRightTopY - innerLeftTopY);
      startX = innerLeftTopX + t * (innerRightTopX - innerLeftTopX);
    }

    // Clip right side if above the angled edge
    if (gy < innerRightTopY) {
      // This horizontal line is above the right corner, need to clip from the angled edge
      const t = (gy - innerLeftTopY) / (innerRightTopY - innerLeftTopY);
      endX = innerLeftTopX + t * (innerRightTopX - innerLeftTopX);
    }

    const grillWidth = endX - startX;

    if (grillWidth > 0) {
      svg.append('rect')
        .attr('x', startX)
        .attr('y', gy - grillThickness / 2)
        .attr('width', grillWidth)
        .attr('height', grillThickness)
        .attr('fill', lineColor)
        .attr('stroke', borderColor)
        .attr('stroke-width', borderWidth);
    }
  }
}
export function drawColonialGrillsTrapezoid(
  svg, x, y, width, height, frameWidth,
  horLines, verLines, lineColor, borderColor, borderWidth,
  grillThickness, shapeOrientation, innerPoints
) {
  if (!innerPoints || innerPoints.length !== 4) {
    console.error("drawColonialGrillsTrapezoid: innerPoints missing or invalid");
    return;
  }

  // Inner polygon points from shapeTrapezoid
  // For trapezoid: top edge is HORIZONTAL (parallel to bottom)
  // A2 = bottom-left, B2 = top-left, C2 = top-right, D2 = bottom-right
  const [A2, B2, C2, D2] = innerPoints;

  const innerLeftTopX = B2[0];
  const innerLeftTopY = B2[1];

  const innerRightTopX = C2[0];
  const innerRightTopY = C2[1]; // Should equal innerLeftTopY for trapezoid (horizontal top)

  const innerBottomY = A2[1]; // same as D2[1]

  const innerLeftX = A2[0]; // bottom-left X
  const innerRightX = D2[0]; // bottom-right X

  // ============================================================
  // VERTICAL GRILLS — CLIPPED TO INNER TRAPEZOID SHAPE
  // ============================================================
  for (let i = 1; i <= verLines; i++) {
    const t = i / (verLines + 1);

    // X position distributed across bottom width
    const gx = innerLeftX + t * (innerRightX - innerLeftX);

    // For trapezoid with horizontal top edge, determine if grill is in diagonal region
    // Top edge spans from innerLeftTopX to innerRightTopX (both at same Y)

    let topY = innerLeftTopY; // default for vertical region
    let grillHeight = innerBottomY - topY;

    // Check if grill is in diagonal region based on orientation
    if (shapeOrientation === 'left') {
      // Left trapezoid: diagonal on left side
      // Diagonal goes from (innerLeftX, innerBottomY) to (innerLeftTopX, innerLeftTopY)
      // If gx < innerLeftTopX, we're in the diagonal region
      if (gx < innerLeftTopX) {
        // Calculate where this vertical grill intersects the diagonal left edge
        const t_diag = (gx - innerLeftX) / (innerLeftTopX - innerLeftX);
        topY = innerBottomY + t_diag * (innerLeftTopY - innerBottomY);
        grillHeight = innerBottomY - topY;
      }
    } else if (shapeOrientation === 'right') {
      // Right trapezoid: diagonal on right side
      // Diagonal goes from (innerRightX, innerBottomY) to (innerRightTopX, innerRightTopY)
      // If gx > innerRightTopX, we're in the diagonal region
      if (gx > innerRightTopX) {
        // Calculate where this vertical grill intersects the diagonal right edge
        const t_diag = (gx - innerRightTopX) / (innerRightX - innerRightTopX);
        topY = innerRightTopY + t_diag * (innerBottomY - innerRightTopY);
        grillHeight = innerBottomY - topY;
      }
    }

    if (grillHeight > 0) {
      svg.append('rect')
        .attr('x', gx - grillThickness / 2)
        .attr('y', topY)
        .attr('width', grillThickness)
        .attr('height', grillHeight)
        .attr('fill', lineColor)
        .attr('stroke', borderColor)
        .attr('stroke-width', borderWidth);
    }
  }

  // ============================================================
  // HORIZONTAL GRILLS — CLIPPED TO INNER TRAPEZOID SHAPE
  // ============================================================
  for (let i = 1; i <= horLines; i++) {
    const gy = innerLeftTopY + (innerBottomY - innerLeftTopY) * (i / (horLines + 1));

    let startX = innerLeftX;
    let endX = innerRightX;

    // Clip based on diagonal edges
    if (shapeOrientation === 'left') {
      // Left trapezoid: diagonal on left side
      // If gy is above the diagonal region, clip left side
      if (gy < innerBottomY) {
        const t_diag = (gy - innerLeftTopY) / (innerBottomY - innerLeftTopY);
        startX = innerLeftTopX + t_diag * (innerLeftX - innerLeftTopX);
      }
    } else if (shapeOrientation === 'right') {
      // Right trapezoid: diagonal on right side
      // If gy is above the diagonal region, clip right side
      if (gy < innerBottomY) {
        const t_diag = (gy - innerRightTopY) / (innerBottomY - innerRightTopY);
        endX = innerRightTopX + t_diag * (innerRightX - innerRightTopX);
      }
    }

    const grillWidth = endX - startX;

    if (grillWidth > 0) {
      svg.append('rect')
        .attr('x', startX)
        .attr('y', gy - grillThickness / 2)
        .attr('width', grillWidth)
        .attr('height', grillThickness)
        .attr('fill', lineColor)
        .attr('stroke', borderColor)
        .attr('stroke-width', borderWidth);
    }
  }
}
export function drawColonialGrillsHalfEyebrow(
  svg, x, y, width, height, frameWidth,
  horLines, verLines, lineColor, borderColor, borderWidth,
  grillThickness, shapeOrientation, innerPoints
) {
  if (!innerPoints || innerPoints.length < 3) {
    console.error("drawColonialGrillsHalfEyebrow: innerPoints missing or invalid (expected array of points along the curve)");
    return;
  }
  if (verLines % 2 === 0) {
  console.log("even");
    verLines = verLines;

  }
  else{
   console.log("odd");   
  }

  // innerPoints contains points along the ellipse curve
  // We need to find the bounding box
  const allX = innerPoints.map(p => p[0]);
  const allY = innerPoints.map(p => p[1]);
  const minX = Math.min(...allX);
  const maxX = Math.max(...allX);
  const minY = Math.min(...allY);
  const maxY = Math.max(...allY);

  // Create clip path using the actual inner curve points
  // Extend the clip path to cover the full rectangular area
  const clipId = `halfEyebrowClip_${Math.random().toString(36).substr(2, 9)}`;

  // Create extended polygon that includes the arch curve and full rectangular extension
  const extendedPoints = [...innerPoints];

  // Add corner points to extend the clip area to full height
  if (shapeOrientation === 'left') {
    // For left orientation, extend to the right side
    extendedPoints.push([maxX, y + height]);
    extendedPoints.push([minX, y + height]);
  } else {
    // For right orientation, extend to the left side (to x)
    extendedPoints.push([maxX, y + height]);
    extendedPoints.push([x, y + height]);
    extendedPoints.push([x, minY]);
  }

  const innerPointsStr = extendedPoints.map(p => p.join(',')).join(' ');

  svg.append('defs')
    .append('clipPath')
    .attr('id', clipId)
    .append('polygon')
    .attr('points', innerPointsStr);

  const grillGroup = svg.append('g')
    .attr('clip-path', `url(#${clipId})`);

  // ============================================================
  // VERTICAL GRILLS — CLIPPED TO INNER HALF EYEBROW SHAPE
  // ============================================================
  for (let i = 1; i <= verLines; i++) {
    const t = i / (verLines + 1);
    const gx = x + t * width;

    grillGroup.append('rect')
      .attr('x', gx - grillThickness / 2)
      .attr('y', y)
      .attr('width', grillThickness)
      .attr('height', 480)
      .attr('fill', lineColor)
      .attr('stroke', borderColor)
      .attr('stroke-width', borderWidth);
  }

  // ============================================================
  // HORIZONTAL GRILLS — CLIPPED TO INNER HALF EYEBROW SHAPE
  // ============================================================
  // Use full width and height - the clip path will handle the arch curve
  for (let i = 1; i <= horLines; i++) {
    const t = i / (horLines + 1);
    const gy = y + t * height;

    grillGroup.append('rect')
      .attr('x', x)
      .attr('y', gy - grillThickness / 2)
      .attr('width', width)
      .attr('height', grillThickness)
      .attr('fill', lineColor)
      .attr('stroke', borderColor)
      .attr('stroke-width', borderWidth);
  }
}

export function drawColonialGrillsEyebrow(
  svg, x, y, width, height, frameWidth,
  horLines, verLines, lineColor, borderColor, borderWidth,
  grillThickness, innerPoints
) {
  if (!innerPoints || innerPoints.length < 3) {
    console.error("drawColonialGrillsEyebrow: innerPoints missing or invalid (expected array of points along the curve)");
    return;
  }

  // innerPoints contains points along the ellipse curve
  // We need to find the bounding box
  const allX = innerPoints.map(p => p[0]);
  const allY = innerPoints.map(p => p[1]);
  const minX = Math.min(...allX);
  const maxX = Math.max(...allX);
  const minY = Math.min(...allY);
  const maxY = Math.max(...allY);

  // Create clip path using the actual inner curve points
  const clipId = `eyebrowClip_${Math.random().toString(36).substr(2, 9)}`;

  // Use innerPoints directly as they already form a closed polygon for the full Eyebrow shape
  const innerPointsStr = innerPoints.map(p => p.join(',')).join(' ');

  svg.append('defs')
    .append('clipPath')
    .attr('id', clipId)
    .append('polygon')
    .attr('points', innerPointsStr);

  const grillGroup = svg.append('g')
    .attr('clip-path', `url(#${clipId})`);

  // ============================================================
  // VERTICAL GRILLS — CLIPPED TO INNER EYEBROW SHAPE
  // ============================================================
  for (let i = 1; i <= verLines; i++) {
    const t = i / (verLines + 1);
    const gx = x + t * width;

    grillGroup.append('rect')
      .attr('x', gx - grillThickness / 2)
      .attr('y', y)
      .attr('width', grillThickness)
      .attr('height', 480)
      .attr('fill', lineColor)
      .attr('stroke', borderColor)
      .attr('stroke-width', borderWidth);
  }

  // ============================================================
  // HORIZONTAL GRILLS — CLIPPED TO INNER EYEBROW SHAPE
  // ============================================================
  for (let i = 1; i <= horLines; i++) {
    const t = i / (horLines + 1);
    const gy = y + t * height;

    grillGroup.append('rect')
      .attr('x', x)
      .attr('y', gy - grillThickness / 2)
      .attr('width', width)
      .attr('height', grillThickness)
      .attr('fill', lineColor)
      .attr('stroke', borderColor)
      .attr('stroke-width', borderWidth);
  }
}

export function drawColonialGrills(
  svg,
  x,
  y,
  width,
  height,
  frameWidth = 10,
  horLines = 1,
  verLines = 1,
  lineColor = 'white',
  borderColor = 'black',
  borderWidth = 0.2,
  grillThickness = 2,
  shape = 'rect',
  shapeOrientation = 'left',
  y1 = 0,        // height offset for left side top point
  y2 = 0,        // height offset for right side top point
  innerPoints
) {
  if (shape === 'Rectangle') {
    drawColonialGrillsRectangle(
      svg, x, y, width, height, frameWidth,
      horLines, verLines, lineColor, borderColor, borderWidth,
      grillThickness
    );
  }

  if (shape === 'Triangle') {
    drawColonialGrillsTriangle(
      svg, x, y, width, height, frameWidth,
      horLines, verLines, lineColor, borderColor, borderWidth,
      grillThickness, shapeOrientation, innerPoints
    );
  }

  if (shape === 'Trapezoid') {
    drawColonialGrillsTrapezoid(
      svg, x, y, width, height, frameWidth,
      horLines, verLines, lineColor, borderColor, borderWidth,
      grillThickness, shapeOrientation, innerPoints
    );
  }

  if (shape === 'AngledTopTrapezoid') {
 drawColonialGrillsAngledTopTrapezoid(
  svg, x, y, width, height, frameWidth,
  horLines, verLines, lineColor, borderColor, borderWidth,
  grillThickness, shapeOrientation, y1, y2, innerPoints
);
  }

  if (shape === 'Octagon') {
    drawColonialGrillsOctagon(
      svg, x, y, width, height, frameWidth,
      horLines, verLines, lineColor, borderColor, borderWidth,
      grillThickness, innerPoints
    );
  }

  if (shape === 'HalfEyebrow') {
    drawColonialGrillsHalfEyebrow(
      svg, x, y, width, height, frameWidth,
      horLines, verLines, lineColor, borderColor, borderWidth,
      grillThickness, shapeOrientation, innerPoints
    );
  }

  if (shape === 'Eyebrow') {
    drawColonialGrillsEyebrow(
      svg, x, y, width, height, frameWidth,
      horLines, verLines, lineColor, borderColor, borderWidth,
      grillThickness, innerPoints
    );
  }

}

  